import { ChevronLeft, SearchMd } from "@untitled-ui/icons-react";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";

import UserCard from "@/components/cards/UserCard";
import Button from "@/components/forms/Button";
import Input from "@/components/forms/Input";
import Carousel from "@/components/helpers/Carousel";
import Legend from "@/components/helpers/Legend";
import UserAvatar from "@/components/media/UserAvatar";
import { useAdvanceSearch } from "@/hooks";
import { cn } from "@/lib/utils";
import { type User } from "@/types";
import { useFetchUsers } from "@/user/services";

import { useRecentSearchedUsers } from "@/modules/messenger/user/hooks/useRecentSearchedUsers";

interface MesssengerSearchSectionProps {
  onClose: () => void;
  onUserClick: (user: User<true>) => void;
  className?: string;
  headerClassName?: string;
}

export default function MesssengerSearchSection({
  onClose,
  onUserClick,
  className,
  headerClassName,
}: MesssengerSearchSectionProps) {
  const { t } = useTranslation("messenger.user");

  const searchData = useAdvanceSearch();
  const { recentSearchedUsers, addRecentUser } = useRecentSearchedUsers();

  const { data: users } = useFetchUsers(searchData.debounced);

  const handleUserClick = (user: User<true>) => {
    addRecentUser(user);
    onUserClick(user);
  };

  return (
    <div className={cn("flex h-full w-full flex-col py-2", className)}>
      <div
        className={cn(
          "flex w-full items-center justify-between gap-3 ps-3.5 pe-6",
          headerClassName,
        )}
      >
        <Button
          onClick={onClose}
          color="link-gray"
          className="text-sm font-semibold text-gray-700"
        >
          <ChevronLeft className="size-6 rtl:rotate-180" />
        </Button>
        <Input
          startIcon={<SearchMd className="size-5" />}
          placeholder={t("Search", { ns: "core.shared" })}
          value={searchData.search.query || ""}
          onChange={(e) => searchData.setQuery(e.target.value)}
          autoFocus
        />
      </div>
      <div className="pt-3">
        <Legend />
      </div>
      <span className="bg-gray-50 px-6 py-2 text-sm/5 font-medium text-gray-600">
        {t("Recent Contacts")}
      </span>
      <div className="flex flex-col gap-4 overflow-y-auto p-4">
        {searchData.search.query ? (
          <div>
            <div className="flex flex-col gap-4">
              {users?.data.map((user: any, i: number) => (
                <Fragment key={i}>
                  <UserCard
                    key={user.id}
                    user={user}
                    size="lg"
                    onClick={() => handleUserClick(user)}
                  />
                </Fragment>
              ))}
            </div>
          </div>
        ) : (
          <Carousel
            slides={recentSearchedUsers.map((user: User<true>) => (
              <div
                key={user.id}
                onClick={() => handleUserClick(user)}
                className="flex flex-col gap-1 p-2"
              >
                <UserAvatar user={user} size="xl" disableTooltip />
                <span className="text-center">
                  {user.first_name} <br /> {user.last_name}
                </span>
              </div>
            ))}
            spaceBetween={2}
            slidesPerView={4}
            loop={false}
            navigation={false}
            pagination={false}
            scrollbar={false}
          />
        )}
      </div>
    </div>
  );
}
